import { properFullname } from "../../../../../services/utilities";

export const getBranchId = (activePlatform = {}) =>
  activePlatform?.branchId || activePlatform?.branch?._id || "";

export const getClinicIds = ({
  activePlatform = {},
  clinics = [],
  authId = "",
} = {}) => {
  const activeClinicIds = [
    activePlatform?.clinic?._id,
    ...(Array.isArray(activePlatform?.clinics)
      ? activePlatform.clinics.map((clinic) => clinic?._id || clinic)
      : []),
  ]
    .map((value) => String(value || ""))
    .filter(Boolean);

  const secretaryClinicIds = (Array.isArray(clinics) ? clinics : [])
    .filter((clinic) =>
      (clinic?.secretaries || []).some(
        (secretary) =>
          String(secretary?._id || secretary) === String(authId || ""),
      ),
    )
    .map((clinic) => String(clinic?._id || ""))
    .filter(Boolean);

  return Array.from(new Set([...activeClinicIds, ...secretaryClinicIds]));
};

export const formatPersonName = (person = {}) => {
  const primaryName = properFullname(person?.fullName || {});

  if (primaryName && primaryName !== "-") {
    return primaryName;
  }

  return (
    person?.name ||
    person?.displayName ||
    person?.title ||
    person?.abbreviation ||
    person?.email ||
    "Unassigned"
  );
};

export const formatDateTime = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleString("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const formatDate = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const getMedicineDosage = (menu = {}) => {
  if (menu?.dosage) return menu.dosage;
  if (String(menu?.itemType || "").toLowerCase() !== "meds") return "";
  if (!menu?.strength) return menu?.unit || "";

  return `${menu.strength} ${menu.measure || ""}/${menu.unit || "pcs"}`
    .replace(/\s+/g, " ")
    .trim();
};

export const getCurrentMonth = () => new Date().getMonth() + 1;

export const getCurrentYear = () => new Date().getFullYear();

export const getMonthOptions = () => [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

export const getStatusColor = (status = "") => {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "approve" || normalized === "available") return "success";
  if (normalized === "submit") return "info";
  if (normalized === "draft") return "warning";
  if (normalized === "cancelled" || normalized === "damaged") return "danger";
  if (normalized === "expired") return "secondary";

  return "default";
};

export const getAvailableQuantity = (inventory = {}) => {
  const consumed = Array.isArray(inventory?.consumptions)
    ? inventory.consumptions.reduce(
        (sum, item) => sum + Number(item?.qnty || 0),
        0,
      )
    : 0;

  return Math.max(Number(inventory?.qnty || 0) - consumed, 0);
};

export const groupInventoriesBySupply = (collections = []) => {
  const grouped = new Map();

  (Array.isArray(collections) ? collections : []).forEach((inventory) => {
    const supply = inventory?.supplies || {};
    const supplyId = String(supply?._id || inventory?.supplies || "");

    if (!supplyId) return;

    if (!grouped.has(supplyId)) {
      grouped.set(supplyId, {
        _id: supplyId,
        supply,
        inventories: [],
      });
    }

    grouped.get(supplyId).inventories.push(inventory);
  });

  return Array.from(grouped.values())
    .map((item) => {
      const availableUnits = item.inventories.reduce(
        (sum, inventory) => sum + getAvailableQuantity(inventory),
        0,
      );

      return {
        ...item,
        availableUnits,
        inventoryCount: item.inventories.length,
      };
    })
    .sort((left, right) => {
      const leftDate = new Date(
        left?.supply?.createdAt || left?.inventories?.[0]?.createdAt || 0,
      ).getTime();
      const rightDate = new Date(
        right?.supply?.createdAt || right?.inventories?.[0]?.createdAt || 0,
      ).getTime();

      return rightDate - leftDate;
    });
};

export const getYearOptions = (groups = []) => {
  const years = new Set([getCurrentYear()]);

  (Array.isArray(groups) ? groups : []).forEach(({ supply = {}, inventories = [] }) => {
    const sourceDate = supply?.createdAt || inventories?.[0]?.createdAt;
    const date = new Date(sourceDate);

    if (!Number.isNaN(date.getTime())) {
      years.add(date.getFullYear());
    }
  });

  return Array.from(years).sort((left, right) => right - left);
};

export const filterSupplyGroupsByMonthYear = (
  groups = [],
  month = getCurrentMonth(),
  year = getCurrentYear(),
) =>
  (Array.isArray(groups) ? groups : []).filter(
    ({ supply = {}, inventories = [] }) => {
      const sourceDate = supply?.createdAt || inventories?.[0]?.createdAt;
      const date = new Date(sourceDate);

      if (Number.isNaN(date.getTime())) return false;

      return (
        date.getMonth() + 1 === Number(month || 0) &&
        date.getFullYear() === Number(year || 0)
      );
    },
  );

export const filterSupplyGroups = filterSupplyGroupsByMonthYear;
